// ---------------------------------------------------------------------------
// leadsMenue — «Leads» als eigener Punkt im Hauptmenue (LEAD-23)
// ---------------------------------------------------------------------------
// Marvin, 07.10.2026: «Leads als eigenes Menü (hauptmenü) weniger vom home».
//
// Gemessen am 07.10.2026 (`notification_items`): von Marvins 58 Zeilen auf der
// Startseite gehoeren 47 zu Anfragen, bei Corsin 19 von 63, bei Sandro 44 von
// 84. Die Startseite war eine Lead-Liste mit etwas anderem dazwischen.
//
// Diese Datei ist die EINE Regel dafuer:
//
//   * WELCHE Karten ins Leads-Menue gehoeren (`LEADS_MENUE_KARTEN`) — die
//     Registry traegt dasselbe als `imLeadsMenue`, ein Test haelt beide gleich.
//   * WER das Menue hat (`hatLeadsMenue`) — wer es hat, findet die Karten
//     dort und nicht mehr auf der Startseite; wer es nicht hat (Buchhaltung),
//     behaelt sie, wo sie sind.
//   * WIE die Seite geordnet ist, steht seit LEAD-25 in `leadsHeute.ts`
//     (`buildLeadHeuteUebersicht`) — jede Karte haengt an genau einer Anfrage
//     oder steht im Auffang. Es gibt keinen Weg, auf dem eine Karte die
//     Startseite verlaesst und im Menue nicht ankommt.
//
// Die Karten bleiben materialisiert: Push, «Jetzt dran» am Projekt und der
// Waechter `karten-wahrheit` lesen dieselben Zeilen wie zuvor. Geaendert hat
// sich der ORT, an dem man sie abarbeitet.
//
// Pure TS, nur Typ-Importe — die Datei liegt im Startbuendel (die Leiste
// zaehlt mit ihr) und darf den Meldungs-Apparat nicht mitziehen.

import type { NotificationType } from "../schemas/notifications";
import type { UserRole } from "../schemas/enums";
import type { UnassignedLeadItem } from "./leadDistribution";

export const LEADS_MENUE_KARTEN = [
  "project_new_from_website",
  "lead_pl_needed",
  "lead_intake_no_contact",
  "lead_assigned",
  "roof_insight_draft",
  "lead_decide_pending",
  "roof_auto_sent_followup",
  "project_lead_pl_stale",
  "project_lead_critical",
] as const satisfies readonly NotificationType[];

export type LeadsMenueKartenTyp = (typeof LEADS_MENUE_KARTEN)[number];

const KARTEN_SET: ReadonlySet<string> = new Set(LEADS_MENUE_KARTEN);

export function istLeadsMenueKarte(type: string): boolean {
  return KARTEN_SET.has(type);
}

/** Projektleitung und Geschaeftsfuehrung. Die Buchhaltung nicht. */
export function hatLeadsMenue(role: UserRole | undefined): boolean {
  return role === "pl" || role === "admin";
}

/** Was eine Karte fuer diese Regel hergeben muss. */
export interface LeadKarteLike {
  readonly type: string;
  readonly projectId?: unknown;
}

export function leadKarteProjektId(karte: LeadKarteLike): string | null {
  return typeof karte.projectId === "string" ? karte.projectId : null;
}

/**
 * Die Startseite OHNE die Karten, die im Leads-Menue stehen — fuer alle, die
 * das Menue haben. Alle anderen bekommen die Liste unveraendert zurueck.
 */
export function ohneLeadsMenueKarten<T extends LeadKarteLike>(
  items: readonly T[],
  role: UserRole | undefined,
): readonly T[] {
  if (!hatLeadsMenue(role)) return items;
  return items.filter((item) => !istLeadsMenueKarte(item.type));
}

// ---------------------------------------------------------------------------
// Die offene Anfrage — in welchem Korb sie liegt, sagt `leadsHeute.ts` (LEAD-25)
// ---------------------------------------------------------------------------

/**
 * Eine offene Anfrage, wie `GET /api/projects/open-leads` sie liefert.
 *
 * Ohne `customerSignal`: die Liste zeigt es nicht, und es zu laden kostet zwei
 * Abfragen pro Anfrage (siehe `loadLeadFacts`). Wer es braucht, ist die Kachel
 * «Wer übernimmt?» — die traegt es weiterhin.
 */
export interface OpenLeadItem extends Omit<UnassignedLeadItem, "customerSignal"> {
  /**
   * `offered` (NACHFASS-1): der Automat hat den Richtpreis versendet, noch
   * fuehrt kein Projektleiter — der Vorgang bleibt zum Nachfassen in der Liste.
   */
  readonly status: "lead" | "lead_in_contact" | "offered";
  readonly projectCode: string | null;
  readonly city: string | null;
  readonly ownerUserId: string | null;
  readonly ownerFullName: string | null;
  /** DATEN-3: seit 180 Tagen ohne jeden Beleg. */
  readonly ruhend: boolean;
  /**
   * LEAD-26: der Merker von «Behalten» (OD-1, `lead_keep:<Ruhe-Anker>`) —
   * derselbe, den der Hinweis «seit n Tagen ohne Bewegung — abgelehnt?» der
   * Projektliste liest. Fehlt in aelteren Antworten.
   */
  readonly keepFingerprint?: string;
  /** Dieser Ruhe-Stand ist mit «Behalten» quittiert — bis sich etwas bewegt. */
  readonly behalten?: boolean;
}
